// Razorpay checkout helper.
// Loads the Razorpay Checkout script on demand and orchestrates the
// create-order → open checkout → verify-signature flow.

const RAZORPAY_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const s = document.createElement('script');
    s.src = RAZORPAY_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function fetchPaymentConfig() {
  try {
    const res = await fetch(`${API}/payments/config`);
    if (!res.ok) return { configured: false, test_mode: false };
    return await res.json();
  } catch {
    return { configured: false, test_mode: false };
  }
}

/**
 * Start a Razorpay payment flow.
 * @param {Object} params
 * @param {Object} params.order - { plan_key, tree_key, sender_name, recipient_name, occasion, message }
 * @param {Object} params.prefill - { name, email, contact }
 * @param {Object} params.theme - { color }
 * @returns {Promise<{ok:boolean, order_id?:string, paid_at?:string, error?:string}>}
 */
export async function startRazorpayPayment({ order, prefill = {}, theme = { color: '#2D4A32' } }) {
  const scriptOk = await loadRazorpayScript();
  if (!scriptOk) return { ok: false, error: 'Could not load Razorpay. Please check your internet connection.' };

  // 1. Create order on backend
  let createData;
  try {
    const createRes = await fetch(`${API}/payments/create-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(order),
    });
    createData = await createRes.json();
    if (!createRes.ok) return { ok: false, error: createData.detail || 'Could not create order.' };
  } catch (e) {
    return { ok: false, error: 'Network error while creating order.' };
  }

  // 2. Open Razorpay checkout — wait for the user to complete or dismiss
  return new Promise((resolve) => {
    const options = {
      key: createData.key_id,
      amount: createData.amount,
      currency: createData.currency,
      name: 'Pallavit',
      description: `${order.plan_key} plan — ${order.tree_key} tree for ${order.recipient_name}`,
      order_id: createData.razorpay_order_id,
      prefill,
      theme,
      handler: async (res) => {
        // 3. Verify signature on backend
        try {
          const verifyRes = await fetch(`${API}/payments/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              order_id: createData.order_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            }),
          });
          const verifyData = await verifyRes.json();
          if (verifyRes.ok && verifyData.status === 'paid') {
            resolve({ ok: true, order_id: verifyData.order_id, paid_at: verifyData.paid_at });
          } else {
            resolve({ ok: false, error: verifyData.detail || 'Payment verification failed.' });
          }
        } catch (e) {
          resolve({ ok: false, error: 'Could not reach server for verification.' });
        }
      },
      modal: {
        ondismiss: () => resolve({ ok: false, error: 'Payment cancelled.' }),
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp) => {
      resolve({ ok: false, error: resp?.error?.description || 'Payment failed.' });
    });
    rzp.open();
  });
}
